import { createPortal } from 'react-dom';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type DragEvent } from 'react';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, EllipsisVertical, Folder } from 'lucide-react';
import DocCard from './DocCard';
import { GlobalFile } from '../../../api/types';
import Alert from '../../../components/Alert';
import { useIsMobile } from '../../../utils/hooks';
import {
  isWorkspaceDragEvent,
  parseWorkspaceDragPayload,
  setWorkspaceDragData,
  type WorkspaceDragPayload,
} from '../utils/workspaceDrag';
import { computeFixedDropdownNearAnchor, scrollableAncestors } from '../utils/dropdownAnchor';

interface DocListProps {
  handleShowEdit: (show: boolean) => void;
  isEmpty: boolean;
  files: GlobalFile[];
  onRequestDelete: (file: GlobalFile) => void;
  onRequestMove?: (file: GlobalFile) => void;
  onRequestMoveFolder?: (folderPath: string, folderName: string) => void;
  onFileUpdated?: () => void;
  onInvoiceFileClick?: (file: GlobalFile) => void;
  onRequestQboSync?: (file: GlobalFile) => void;
  onBulkDelete?: (files: GlobalFile[]) => void;
  onBulkConfirm?: (files: GlobalFile[]) => void;
  onBulkMove?: (files: GlobalFile[]) => void;
  businessId?: string;
  availableTagLabels?: string[];
  onLabelsChanged?: () => void;
  currentPage?: number;
  totalPages?: number;
  totalResults?: number;
  pageSize?: number;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  onPageChange?: (page: number) => void;
  onShowMetadata?: (file: GlobalFile) => void;
  /** Open full main-area Google Sheets editor (Finz workspace layout). */
  onGoogleSheetWorkspace?: (file: GlobalFile) => void;
  /** Move files/folders when dropped on a folder row (async S3 on backend). */
  onWorkspaceDrop?: (targetFolderPath: string, payload: WorkspaceDragPayload) => void | Promise<void>;
  folderRows?: Array<{
    key: string;
    name: string;
    categoryKey?: string;
    itemCountLabel: string;
    lastModifiedLabel?: string;
    folderPath?: string;
    pathLabel?: string;
    onOpen: () => void;
    dropTargetPath?: string;
    folderDragSourcePath?: string;
    renameableFolderPath?: string;
  }>;
}

type FolderRowModel = NonNullable<DocListProps['folderRows']>[number];

function WorkspaceFolderActions({
  folder,
  onManage,
}: {
  folder: FolderRowModel;
  onManage?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const measureFolderMenu = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return null;
    return computeFixedDropdownNearAnchor(el, {
      isMobile: false,
      dropdownWidth: 176,
      dropdownHeight: 120,
    });
  }, []);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setDropdownPosition(null);
      return;
    }
    const measure = () => {
      const pos = measureFolderMenu();
      if (pos) setDropdownPosition(pos);
    };
    measure();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [menuOpen, measureFolderMenu]);

  useEffect(() => {
    if (!menuOpen) return;
    const reposition = () => {
      const pos = measureFolderMenu();
      if (pos) setDropdownPosition(pos);
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    const ancestors = scrollableAncestors(buttonRef.current);
    for (const node of ancestors) {
      node.addEventListener('scroll', reposition, true);
    }
    reposition();
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      for (const node of ancestors) {
        node.removeEventListener('scroll', reposition, true);
      }
    };
  }, [menuOpen, measureFolderMenu]);

  useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [menuOpen]);

  if (!onManage) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Open actions for ${folder.name}`}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMenuOpen((prev) => !prev);
        }}
        className="shrink-0 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
      >
        <EllipsisVertical className="h-4 w-4" />
      </button>
      {menuOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          role="menu"
          className="fixed z-[9999] min-w-[160px] rounded border bg-white p-3 shadow-lg flex flex-col gap-2 font-normal text-sm"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <button
            type="button"
            role="menuitem"
            className="w-full cursor-pointer rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen(false);
              onManage();
            }}
          >
            Manage
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}

function WorkspaceFolderTableRow({
  folder,
  onWorkspaceDrop,
  onRequestMoveFolder,
}: {
  folder: FolderRowModel;
  onWorkspaceDrop?: (targetFolderPath: string, payload: WorkspaceDragPayload) => void | Promise<void>;
  onRequestMoveFolder?: (folderPath: string, folderName: string) => void;
}) {
  const [dragOver, setDragOver] = useState(false);

  const dropTargetPath = onWorkspaceDrop ? folder.dropTargetPath : undefined;
  const folderDragSourcePath = onWorkspaceDrop ? folder.folderDragSourcePath : undefined;
  const canManage = Boolean(folder.folderPath && onRequestMoveFolder);

  const handleDragOverCapture = (e: DragEvent) => {
    if (!dropTargetPath || !onWorkspaceDrop) return;
    if (!isWorkspaceDragEvent(e)) {
      const types = Array.from(e.dataTransfer?.types || []);
      if (types.includes('Files')) return;
      return;
    }
    const payload = parseWorkspaceDragPayload(e);
    if (payload?.sourceCategoryKey && folder.categoryKey && payload.sourceCategoryKey !== folder.categoryKey) {
      setDragOver(false);
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    setDragOver(false);
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
  };

  const handleDrop = (e: DragEvent) => {
    setDragOver(false);
    if (!dropTargetPath || !onWorkspaceDrop) return;
    const payload = parseWorkspaceDragPayload(e);
    if (!payload) return;
    e.preventDefault();
    if (payload.sourceCategoryKey && folder.categoryKey && payload.sourceCategoryKey !== folder.categoryKey) {
      return;
    }
    if (
      payload.kind === 'folder' &&
      payload.sourceFolderPath.replace(/\/+$/, '') === dropTargetPath.replace(/\/+$/, '')
    ) {
      return;
    }
    void onWorkspaceDrop(dropTargetPath, payload);
  };

  const handleFolderDragStart = (e: DragEvent) => {
    if (!folderDragSourcePath || !onWorkspaceDrop) return;
    setWorkspaceDragData(e.dataTransfer, {
      kind: 'folder',
      sourceFolderPath: folderDragSourcePath,
      sourceCategoryKey: folder.categoryKey,
    }, folder.name);
  };

  const rowGrid =
    'grid-cols-[minmax(200px,2fr)_minmax(120px,0.95fr)_minmax(168px,1.15fr)_minmax(92px,0.7fr)_minmax(128px,1fr)_minmax(96px,0.85fr)_48px]';

  return (
    <div
      draggable={Boolean(folderDragSourcePath && onWorkspaceDrop)}
      onDragStart={folderDragSourcePath ? handleFolderDragStart : undefined}
      onDragOverCapture={handleDragOverCapture}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => {
        folder.onOpen();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          folder.onOpen();
        }
      }}
      role="button"
      tabIndex={0}
      className={`relative grid min-h-[60px] w-full min-w-[1200px] items-center border-b border-gray-200 text-[#4C545F] font-inter font-medium text-[14px] leading-[120%] tracking-normal text-left ${rowGrid} ${dragOver && dropTargetPath ? 'ring-2 ring-inset ring-indigo-400 bg-indigo-50/30' : ''
        } hover:bg-gray-50/90 transition-colors`}
    >
      <div className="pl-6 pr-5 py-3 min-w-0">
        <div className="flex items-center gap-2 min-w-0 h-full">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                folder.onOpen();
              }}
              className="inline-flex min-w-0 max-w-full items-center gap-2 text-left text-gray-900"
            >
              <Folder className="h-4 w-4 shrink-0 text-amber-600" />
              <span className="truncate font-medium">{folder.name}</span>
            </button>
          </div>
        </div>
      </div>
      <div className="pl-4 pr-6 py-3 text-gray-900 whitespace-nowrap">{folder.lastModifiedLabel || '—'}</div>
      <div className="px-3 py-3 text-sm text-gray-400">—</div>
      <div data-tutorial="documents-type-value" className="px-3 py-3 flex justify-center">
        <span className="inline-flex items-center px-2.5 py-1 rounded text-xs capitalize bg-gray-100 text-gray-700 border border-gray-200">
          Folder
        </span>
      </div>
      <div className="px-3 py-3 min-w-0">
        <div className="text-sm text-gray-600 truncate">{folder.itemCountLabel}</div>
      </div>
      <div className="px-2 py-3" />
      <div
        className="px-2 py-3 flex justify-center"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <WorkspaceFolderActions
          folder={folder}
          onManage={canManage && folder.folderPath ? () => onRequestMoveFolder?.(folder.folderPath!, folder.name) : undefined}
        />
      </div>
    </div>
  );
}

const DocList: React.FC<DocListProps> = ({
  handleShowEdit,
  isEmpty,
  files,
  onRequestDelete,
  onRequestMove,
  onRequestMoveFolder,
  onFileUpdated,
  onInvoiceFileClick,
  onRequestQboSync,
  onBulkDelete,
  onBulkConfirm,
  onBulkMove,
  businessId,
  availableTagLabels = [],
  onLabelsChanged,
  currentPage = 1,
  totalPages = 1,
  totalResults = 0,
  pageSize = 0,
  onPrevPage,
  onNextPage,
  onPageChange,
  onShowMetadata,
  onGoogleSheetWorkspace,
  onWorkspaceDrop,
  folderRows = [],
}) => {
  const isMobile = useIsMobile();
  const [sortAsc, setSortAsc] = useState(false);
  const [pageInput, setPageInput] = useState(String(currentPage));
  const [isEditingPage, setIsEditingPage] = useState(false);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [hasVerticalOverflow, setHasVerticalOverflow] = useState(false);

  useEffect(() => {
    setPageInput(String(currentPage));
    setIsEditingPage(false);
  }, [currentPage]);

  const sortedFiles = [...files].sort((a, b) => {
    const diff = new Date(a.upload_time).getTime() - new Date(b.upload_time).getTime();
    return sortAsc ? diff : -diff;
  });
  const desktopGridCols =
    'grid-cols-[minmax(200px,2fr)_minmax(120px,0.95fr)_minmax(168px,1.15fr)_minmax(92px,0.7fr)_minmax(128px,1fr)_minmax(96px,0.85fr)_48px]';
  const effectiveTotal = totalResults > 0 ? totalResults : sortedFiles.length + folderRows.length;
  const effectivePageSize = pageSize > 0 ? pageSize : sortedFiles.length;
  const pageStart = effectiveTotal > 0 ? (currentPage - 1) * effectivePageSize + 1 : 0;
  const pageEnd = effectiveTotal > 0 ? Math.min(currentPage * effectivePageSize, effectiveTotal) : 0;
  const handlePageSubmit = () => {
    const parsed = Number(pageInput);
    if (!Number.isFinite(parsed)) {
      setPageInput(String(currentPage));
      setIsEditingPage(false);
      return;
    }
    const nextPage = Math.min(Math.max(Math.trunc(parsed), 1), Math.max(totalPages, 1));
    setPageInput(String(nextPage));
    setIsEditingPage(false);
    if (nextPage !== currentPage) {
      if (onPageChange) onPageChange(nextPage);
      else if (nextPage > currentPage) onNextPage?.();
      else onPrevPage?.();
    }
  };

  const hasFolderRows = folderRows.length > 0;
  const hasTableContent = !isEmpty || hasFolderRows;

  useEffect(() => {
    const node = tableScrollRef.current;
    if (!node) {
      setHasVerticalOverflow(false);
      return;
    }

    const updateOverflow = () => {
      setHasVerticalOverflow(node.scrollHeight > node.clientHeight + 1);
    };

    updateOverflow();

    const observer = new ResizeObserver(() => {
      updateOverflow();
    });
    observer.observe(node);

    const inner = node.firstElementChild;
    if (inner instanceof HTMLElement) {
      observer.observe(inner);
    }

    return () => observer.disconnect();
  }, [hasTableContent, sortedFiles.length, folderRows.length, currentPage, isMobile]);

  if (isMobile) {
    return (
      <div className="w-full">
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => setSortAsc((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
          >
            Upload date
            {sortAsc ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
          </button>
        </div>

        {hasTableContent ? (
          <div>
            {folderRows.map((folder) => (
              <button
                key={folder.key}
                type="button"
                onClick={folder.onOpen}
                className="mb-2 flex min-h-[60px] w-full items-center rounded-lg border border-gray-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:bg-gray-50"
              >
                <div className="w-full">
                  <div className="flex items-center justify-between gap-3 text-gray-900">
                    <div className="flex min-w-0 items-center gap-2">
                      <Folder className="h-4 w-4 shrink-0 text-amber-600" />
                      <span className="truncate text-sm font-medium">{folder.name}</span>
                    </div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <WorkspaceFolderActions
                        folder={folder}
                        onManage={folder.folderPath && onRequestMoveFolder ? () => onRequestMoveFolder(folder.folderPath!, folder.name) : undefined}
                      />
                    </div>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                    <span
                      data-tutorial="documents-type-value"
                      className="inline-flex items-center rounded border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700"
                    >
                      Folder
                    </span>
                    <span>{folder.itemCountLabel}</span>
                  </div>
                </div>
              </button>
            ))}
            {sortedFiles.map((file) => (
              <div key={file.file_id}>
                <DocCard
                  file={file}
                  handleShowEdit={handleShowEdit}
                  onRequestDelete={() => onRequestDelete(file)}
                  onRequestMove={onRequestMove}
                  onFileUpdated={onFileUpdated}
                  onReviewInvoice={file.type === 'invoice' || file.type === 'expenses' || file.type === 'bills' ? onInvoiceFileClick : undefined}
                  onRequestQboSync={onRequestQboSync}
                  businessId={businessId}
                  showCheckbox={false}
                  availableTagLabels={availableTagLabels}
                  onLabelsChanged={onLabelsChanged}
                  onShowMetadata={onShowMetadata}
                  onGoogleSheetWorkspace={onGoogleSheetWorkspace}
                  workspaceDragFileIds={file.file_id ? [file.file_id] : undefined}
                  workspaceDragCategoryKey={(file.workspace_category || '').trim().toLowerCase()}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex align-center justify-center items-center flex-col h-full">
            <Alert subtitle={''} label={'No documents'} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <div className="flex flex-col gap-3 h-full">
        <div className="w-full h-full rounded-lg border border-gray-200 bg-white shadow-sm flex flex-col overflow-hidden">
          <div
            ref={tableScrollRef}
            className={`w-full flex-1 min-h-0 overflow-x-auto ${hasTableContent && hasVerticalOverflow ? 'overflow-y-auto scrollbar-hide' : 'overflow-y-hidden'}`}
          >
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
              <div className={`grid min-w-[1200px] text-[#4C545F] font-inter font-medium text-[14px] leading-[120%] tracking-normal ${desktopGridCols}`}>
                <div className="pl-6 pr-5 py-4">Document</div>
                <button
                  type="button"
                  onClick={() => setSortAsc((v) => !v)}
                  className="pl-4 pr-6 py-4 flex items-center gap-1 text-left hover:bg-gray-50 transition-colors"
                >
                  Last modified
                  {sortAsc ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
                </button>
                <div className="px-3 py-4">Source</div>
                <div data-tutorial="documents-type-column" className="px-3 py-4 flex justify-center">Type</div>
                <div className="px-3 py-4">Tags or remarks</div>
                <div className="px-2 py-4 flex justify-center">QBO Sync</div>
                <div className="px-2 py-4" aria-label="Actions" />
              </div>
            </div>

            {hasTableContent ? (
              <div className="min-w-[1200px]">
                {folderRows.map((folder) => (
                  <WorkspaceFolderTableRow
                    key={folder.key}
                    folder={folder}
                    onWorkspaceDrop={onWorkspaceDrop}
                    onRequestMoveFolder={onRequestMoveFolder}
                  />
                ))}
                {sortedFiles.map((file) => (
                  <div key={file.file_id}>
                    <DocCard
                      file={file}
                      handleShowEdit={handleShowEdit}
                      onRequestDelete={() => onRequestDelete(file)}
                      onRequestMove={onRequestMove}
                      onFileUpdated={onFileUpdated}
                      onReviewInvoice={file.type === 'invoice' || file.type === 'expenses' || file.type === 'bills' ? onInvoiceFileClick : undefined}
                      onRequestQboSync={onRequestQboSync}
                      businessId={businessId}
                      showCheckbox={false}
                      availableTagLabels={availableTagLabels}
                      onLabelsChanged={onLabelsChanged}
                      onShowMetadata={onShowMetadata}
                      onGoogleSheetWorkspace={onGoogleSheetWorkspace}
                workspaceDragFileIds={file.file_id ? [file.file_id] : undefined}
                workspaceDragCategoryKey={(file.workspace_category || '').trim().toLowerCase()}
              />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <Alert subtitle={''} label={'No documents'} />
              </div>
            )}
          </div>

          {hasTableContent && (
            <div className="sticky bottom-0 z-10 mt-auto flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3">
              <div className="w-1/3 flex justify-start">
                <span className="text-xs text-gray-500 font-medium">
                  {pageStart} - {pageEnd} of {String(effectiveTotal).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} results
                </span>
              </div>

              <div className="w-1/3 flex items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={onPrevPage}
                  className="w-8 h-6 rounded-md bg-[#F9FAFB] text-gray-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>

                <span className="text-sm text-gray-600">
                  Page{' '}
                  {isEditingPage ? (
                    <input
                      type="number"
                      min={1}
                      max={Math.max(totalPages, 1)}
                      value={pageInput}
                      autoFocus
                      onChange={(e) => setPageInput(e.target.value)}
                      onBlur={handlePageSubmit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handlePageSubmit();
                        if (e.key === 'Escape') {
                          setPageInput(String(currentPage));
                          setIsEditingPage(false);
                        }
                      }}
                      className="mx-1 h-6 w-10 border-0 border-b-2 border-gray-400 bg-transparent px-0 text-center text-sm text-gray-600 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      aria-label="Current page"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsEditingPage(true)}
                      className="mx-1 min-w-[1.25rem] border-b-2 border-gray-300 px-0.5 text-center text-sm text-gray-600 transition-colors hover:border-gray-500"
                      aria-label={`Edit page number, current page ${currentPage}`}
                      title="Click to jump to a page"
                    >
                      {currentPage}
                    </button>
                  )}{' '}
                  of {totalPages}
                </span>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={onNextPage}
                  className="w-8 h-6 rounded-md bg-[#F0F0FF] text-gray-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="w-1/3" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocList
